/**
 * activityUtils — metadata and summary generators for the activity feed.
 *
 * COLLECTION_META maps Firestore collection names to display properties.
 * getSummary(entry) produces a one-line human-readable summary of the entry,
 * written at log time so the feed never has to re-query source collections.
 */

export const COLLECTION_META = {
  bottleFeeds: {
    icon: '🍼',
    label: 'Bottle Feed',
    getSummary: (e) => e.amount ? `${e.amount} oz${e.type ? ` · ${e.type}` : ''}` : 'bottle feed',
  },
  breastfeeding: {
    icon: '🤱',
    label: 'Breastfeeding',
    getSummary: (e) => [e.duration && `${e.duration} min`, e.side].filter(Boolean).join(' · ') || 'session',
  },
  diapers: {
    icon: '🧷',
    label: 'Nappy Change',
    getSummary: (e) => {
      const typeMap = { wet: 'Wet', dirty: 'Dirty', both: 'Wet & dirty' };
      return typeMap[e.type] || e.type || 'change';
    },
  },
  sleepTracking: {
    icon: '😴',
    label: 'Sleep',
    getSummary: (e) => {
      const typeMap = { nap: 'Nap', night: 'Night sleep' };
      const label = typeMap[e.type] || e.type || 'sleep';
      if (e.startTime && e.endTime) return `${label} · ${e.startTime}–${e.endTime}`;
      return label;
    },
  },
  waterIntake: {
    icon: '💧',
    label: 'Water Intake',
    getSummary: (e) => e.amount ? `${e.amount} oz` : 'water',
  },
  solidFoods: {
    icon: '🥘',
    label: 'Solid Food',
    getSummary: (e) => e.food || e.name || 'solid food',
  },
  foodIntroductions: {
    icon: '🍎',
    label: 'Food Introduction',
    getSummary: (e) => {
      const parts = [e.food || e.name];
      if (e.reaction) parts.push(e.reaction);
      return parts.filter(Boolean).join(' · ') || 'new food';
    },
  },
  growthHealth: {
    icon: '📏',
    label: 'Growth Measurement',
    getSummary: (e) => {
      const parts = [];
      if (e.weight) parts.push(`${e.weight} ${e.weightUnit || 'kg'}`);
      if (e.height) parts.push(`${e.height} ${e.heightUnit || 'cm'}`);
      if (e.headCircumference) parts.push(`HC ${e.headCircumference} cm`);
      return parts.join(' · ') || 'measurement';
    },
  },
  milestones: {
    icon: '🎯',
    label: 'Milestone',
    getSummary: (e) => e.title || e.milestone || 'milestone',
  },
  doctorVisits: {
    icon: '🏥',
    label: 'Doctor Visit',
    getSummary: (e) => e.reason || e.doctor || 'visit',
  },
  vaccinations: {
    icon: '💉',
    label: 'Vaccination',
    getSummary: (e) => e.vaccine || e.name || 'vaccination',
  },
  allergies: {
    icon: '⚠️',
    label: 'Allergy Logged',
    getSummary: (e) => e.allergen || e.food || 'allergy',
  },
  healthRecords: {
    icon: '📋',
    label: 'Health Record',
    getSummary: (e) => {
      if (e.temperature) return `Temp: ${e.temperature}°`;
      if (e.medication) return `Medication: ${e.medication}`;
      return e.type || 'health record';
    },
  },
};

/**
 * Returns a relative time string for the given ISO timestamp.
 * e.g. "just now", "5m ago", "2h ago", "yesterday", "3d ago"
 */
export function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

/**
 * Derives a short display name from a Firebase user object.
 */
export function caregiverName(user) {
  if (!user) return 'Caregiver';
  if (user.displayName) return user.displayName;
  if (user.email) return user.email.split('@')[0];
  return 'Caregiver';
}
